import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import { useAuth } from './AuthContext';

export interface CreditBalance {
  credit_balance: number;
  is_unlimited: boolean;
  is_paid_user: boolean;
}

export interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price_usd: number;
  bonus_percent: number;
  description: string;
  effective_rate: number;
}

export interface CreditEstimate {
  estimated_credits: number;
  breakdown: Record<string, number>;
  is_unlimited: boolean;
  query_type?: string;
  message?: string;
}

export interface CreditTransaction {
  id: string;
  amount: number;
  balance_after: number;
  transaction_type: string;
  description: string;
  query_id: string | null;
  created_at: string | null;
}

interface CreditContextType {
  balance: number | null;
  balanceData: CreditBalance | null;
  isUnlimited: boolean;
  isPaidUser: boolean;
  isLoading: boolean;
  error: string | null;
  packs: CreditPack[];
  fetchBalance: () => Promise<CreditBalance | null>;
  refreshBalance: () => Promise<CreditBalance | null>;
  fetchPacks: () => Promise<CreditPack[]>;
  estimateCost: (
    queryType: 'quick_chat' | 'standard_research' | 'deep_deliberation',
    models?: string[]
  ) => Promise<CreditEstimate | null>;
  purchaseCredits: (packId: string) => Promise<string | null>;
  fetchHistory: (limit?: number, offset?: number) => Promise<{ transactions: CreditTransaction[]; total: number }>;
  hasSufficientCredits: (requiredCredits: number) => boolean;
  clearError: () => void;
}

const CreditContext = createContext<CreditContextType | undefined>(undefined);

// SWR thresholds
const STALE_MS = 30_000;   // 30s: serve cached, background refresh
const EXPIRED_MS = 60_000; // 60s: blocking refresh

export function CreditProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  const [balanceData, setBalanceData] = useState<CreditBalance | null>(null);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lastFetchedAtRef = useRef<number>(0);
  const packsLoadedRef = useRef(false);
  const fetchingRef = useRef(false);
  const balanceDataRef = useRef<CreditBalance | null>(null);

  // Core fetch — always hits the API
  const fetchBalance = useCallback(async (): Promise<CreditBalance | null> => {
    if (fetchingRef.current) return balanceDataRef.current;
    fetchingRef.current = true;
    try {
      const response = await api.getCreditsBalance();
      setBalanceData(response);
      balanceDataRef.current = response;
      setError(null);
      lastFetchedAtRef.current = Date.now();
      return response;
    } catch {
      setError('Failed to load credit balance');
      return null;
    } finally {
      fetchingRef.current = false;
    }
  }, []);

  // SWR-aware fetch: skips if fresh, background-refreshes if stale, blocks if expired
  const fetchBalanceSWR = useCallback(async () => {
    const age = Date.now() - lastFetchedAtRef.current;

    if (age < STALE_MS && balanceData !== null) {
      // Fresh — no-op
      return;
    }

    if (age < EXPIRED_MS && balanceData !== null) {
      // Stale — background refresh, no loading state
      fetchBalance();
      return;
    }

    // Expired or no data — blocking fetch
    setIsLoading(true);
    await fetchBalance();
    setIsLoading(false);
  }, [balanceData, fetchBalance]);

  // Fetch on auth change
  useEffect(() => {
    if (isAuthenticated) {
      fetchBalanceSWR();
    } else {
      // Reset on logout
      setBalanceData(null);
      setPacks([]);
      packsLoadedRef.current = false;
      lastFetchedAtRef.current = 0;
      setIsLoading(false);
      setError(null);
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lazy packs loader
  const fetchPacks = useCallback(async (): Promise<CreditPack[]> => {
    if (packsLoadedRef.current && packs.length > 0) return packs;
    try {
      const response = await api.getCreditPacks();
      const packsWithRate = response.packs.map(pack => ({
        ...pack,
        effective_rate: pack.price_usd / pack.credits,
      }));
      setPacks(packsWithRate);
      packsLoadedRef.current = true;
      return packsWithRate;
    } catch (err) {
      console.error('Failed to fetch credit packs:', err);
      return [];
    }
  }, [packs]);

  const estimateCost = useCallback(async (
    queryType: 'quick_chat' | 'standard_research' | 'deep_deliberation',
    models?: string[]
  ): Promise<CreditEstimate | null> => {
    try {
      const response = await api.estimateCreditCost(queryType, {
        model_preferences: models,
      });
      return {
        estimated_credits: response.estimated_credits,
        breakdown: response.model_info as Record<string, number>,
        is_unlimited: false,
        query_type: response.query_type,
      };
    } catch {
      return null;
    }
  }, []);

  const purchaseCredits = useCallback(async (packId: string): Promise<string | null> => {
    try {
      const response = await api.purchaseCreditPack(packId);
      return response.checkout_url;
    } catch (err) {
      console.error('Failed to initiate purchase:', err);
      setError('Failed to start purchase. Please try again.');
      return null;
    }
  }, []);

  const fetchHistory = useCallback(async (limit = 50, offset = 0) => {
    try {
      const response = await api.getCreditHistory({ limit, offset });
      const transactions: CreditTransaction[] = response.transactions.map(tx => ({
        ...tx,
        query_id: null,
      }));
      return { transactions, total: response.total };
    } catch (err) {
      console.error('Failed to fetch history:', err);
      return { transactions: [], total: 0 };
    }
  }, []);

  const hasSufficientCredits = useCallback((requiredCredits: number): boolean => {
    if (!balanceData) return false;
    if (balanceData.is_unlimited) return true;
    return balanceData.credit_balance >= requiredCredits;
  }, [balanceData]);

  const value: CreditContextType = {
    balance: balanceData?.credit_balance ?? null,
    balanceData,
    isUnlimited: balanceData?.is_unlimited ?? false,
    isPaidUser: balanceData?.is_paid_user ?? false,
    isLoading,
    error,
    packs,
    fetchBalance,
    refreshBalance: fetchBalance,
    fetchPacks,
    estimateCost,
    purchaseCredits,
    fetchHistory,
    hasSufficientCredits,
    clearError: () => setError(null),
  };

  return (
    <CreditContext.Provider value={value}>
      {children}
    </CreditContext.Provider>
  );
}

export function useCreditContext() {
  const context = useContext(CreditContext);
  if (context === undefined) {
    throw new Error('useCreditContext must be used within a CreditProvider');
  }
  return context;
}
